import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { Button, Modal, StatusPill, colors } from 'src/components/app-ui'
import { courseFilterAction } from 'src/reducers/course/action'
import { coursePatchService } from 'src/api/course'
import { getCollectionCourseList } from 'src/reducers/collection/action'
import { PatchContent, PatchData } from 'src/model/patch'
import { getCourseCover } from 'src/pages/courses/components/course-library-card'
import { Link } from 'react-router-dom'

const useStyles = makeStyles({
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: '0 24px 12px',
    padding: '8px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    color: colors.subtle,
  },
  searchInput: {
    flex: 1,
    border: 0,
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: '13px',
    color: colors.navy,
  },
  list: {
    borderTop: `1px solid ${colors.border}`,
    borderBottom: `1px solid ${colors.border}`,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 24px',
    borderBottom: '1px solid #F1F3F5',
    cursor: 'pointer',
  },
  box: {
    width: 18,
    height: 18,
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    color: '#FFFFFF',
  },
  cover: {
    width: 56,
    height: 35,
    flex: 'none',
    borderRadius: '6px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: '14px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    fontSize: '12px',
    color: colors.subtle,
  },
  empty: {
    padding: '32px 24px',
    textAlign: 'center',
    fontSize: '13px',
    color: colors.subtle,
  },
  itemLocked: {
    cursor: 'default',
    background: '#FAFAFB',
  },
  lockNote: {
    flex: '0 1 260px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    fontSize: '12px',
    lineHeight: 1.45,
    color: '#B76E00',
  },
  lockIcon: {
    flex: 'none',
    marginTop: 2,
  },
  lockLink: {
    fontWeight: 600,
    color: '#B76E00',
    textDecoration: 'underline',
  },
  hint: {
    flex: 1,
    fontSize: '12px',
    lineHeight: 1.4,
    color: colors.muted,
  },
})

/**
 * Lists courses that are not in any learning path yet and appends the
 * selected ones to this path (a course belongs to one path at a time).
 */
const AddCoursesDialog = ({ open, onClose, pathId, nextOrder }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const independentCourses = useSelector(
    state => state.course.independentCourseList,
  )
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelected({})
    setQuery('')
    dispatch(
      courseFilterAction({
        filterText: '',
        sortingColumn: 'createdAt',
        asc: false,
        currentPage: 0,
        pageSize: 100,
        independent: true,
        // Courses in other paths are listed too, but can't be picked: a course
        // is in one learning path at a time.
        includeInPaths: true,
        excludeCollectionId: pathId,
      }),
    )
  }, [open, dispatch, pathId])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (independentCourses || []).filter(
      course => !q || (course.name || '').toLowerCase().includes(q),
    )
  }, [independentCourses, query])

  // Keep the original id values (object keys would turn them into strings).
  const selectedIds = (independentCourses || [])
    .filter(course => selected[course.id] && !course.courseCollectionId)
    .map(course => course.id)
  const count = selectedIds.length

  // Patches the courses one by one so a failure is reported (the patch saga
  // swallows errors) and the order stays sequential.
  const handleAdd = async () => {
    setSaving(true)
    let added = 0
    try {
      for (const [index, courseId] of selectedIds.entries()) {
        const patch = new PatchData(null, courseId)
        patch.addContent(
          new PatchContent(
            pathId,
            'courseCollectionId',
            PatchData.OPERATION_REPLACE,
          ),
        )
        patch.addContent(
          new PatchContent(
            nextOrder + index,
            'courseCollectionOrder',
            PatchData.OPERATION_REPLACE,
          ),
        )
        await coursePatchService(patch)
        added += 1
      }
      enqueueSnackbar(
        `${added} ${added === 1 ? 'course' : 'courses'} added to the path.`,
        { variant: 'success' },
      )
      onClose()
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.operationMessage ||
          (added
            ? `Added ${added} of ${selectedIds.length} courses; the rest could not be added.`
            : 'Could not add the courses.'),
        { variant: 'error' },
      )
    } finally {
      if (added) dispatch(getCollectionCourseList(pathId, () => {}))
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth={640}
      title="Add existing courses"
      subtitle="Courses are appended to the end of the path."
      footer={
        <>
          <span className={classes.hint}>
            A course can be in one learning path at a time.
          </span>
          <Button variant="ghost" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" disabled={!count || saving} onClick={handleAdd}>
            {count
              ? `Add ${count} ${count === 1 ? 'course' : 'courses'}`
              : 'Add courses'}
          </Button>
        </>
      }
    >
      <label className={classes.search}>
        <Icon icon="tabler:search" width={14} height={14} />
        <input
          className={classes.searchInput}
          placeholder="Search courses..."
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
      </label>
      <div className={classes.list}>
        {visible.map(course => {
          const inOtherPath = Boolean(course.courseCollectionId)
          const on = !inOtherPath && Boolean(selected[course.id])
          const pages = course.pageCount || 0
          return (
            <div
              key={course.id}
              className={`${classes.item} ${
                inOtherPath ? classes.itemLocked : ''
              }`}
              style={{ background: on ? 'rgba(1,95,251,.04)' : undefined }}
              onClick={() =>
                !inOtherPath &&
                setSelected(current => ({ ...current, [course.id]: !on }))
              }
            >
              <span
                className={classes.box}
                style={{
                  border: on ? 0 : `1.5px solid ${colors.disabled}`,
                  background: on
                    ? colors.primary
                    : inOtherPath
                    ? colors.hover
                    : '#FFFFFF',
                }}
              >
                {on && <Icon icon="tabler:check" width={12} height={12} />}
              </span>
              <div
                className={classes.cover}
                style={{
                  backgroundImage: `url(${getCourseCover(course.thumbnail)})`,
                  opacity: inOtherPath ? 0.6 : 1,
                }}
              />
              <div className={classes.text}>
                <div
                  className={classes.title}
                  style={inOtherPath ? { color: colors.muted } : undefined}
                >
                  {course.name}
                </div>
                <div className={classes.meta}>
                  {pages
                    ? `${pages} ${pages === 1 ? 'page' : 'pages'}`
                    : 'No pages yet'}
                </div>
              </div>
              {inOtherPath && (
                <div className={classes.lockNote}>
                  <Icon
                    icon="tabler:lock"
                    width={13}
                    height={13}
                    className={classes.lockIcon}
                  />
                  <span>
                    In{' '}
                    <Link
                      to={`/collection/${course.courseCollectionId}/mini-courses`}
                      className={classes.lockLink}
                      onClick={event => event.stopPropagation()}
                    >
                      {course.courseCollectionName || 'another learning path'}
                    </Link>
                    . To add it here, remove it from that path first.
                  </span>
                </div>
              )}
              <StatusPill
                status={course.isPublished === false ? 'Draft' : 'Published'}
              />
            </div>
          )
        })}
        {visible.length === 0 && (
          <div className={classes.empty}>
            {query
              ? 'No courses match your search.'
              : 'There are no other courses in this workspace yet.'}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default AddCoursesDialog
